<script setup>
import { computed, ref, watch } from "vue";
import { BIconCheck, BIconPerson } from "bootstrap-icons-vue";
import { arraysEqual } from "@/utils/utils.js";
import IconArrowLoading from "@/components/icons/IconArrowLoading.vue";

import { useUserStore } from "@/stores/user";
import { useTeamStore } from "@/stores/team";

const userStore = useUserStore();
const teamStore = useTeamStore();

const props = defineProps({
  agentList: {
    type: Object,
    default: () => ({}),
  },
  teamList: {
    type: Object,
    default: () => ({}),
  },
  preselectedAgents: {
    type: Array,
    default: () => [],
  },
  preselectedTeams: {
    type: Array,
    default: () => [],
  },
  showTeamsTab: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update-assignees", "update-teams"]);

const isAgentMenuVisible = ref(false);
const isTeamMenuVisible = ref(false);
const searchTeamQuery = ref("");
const searchAgentQuery = ref("");

const selectedAgentIds = ref([]);
const selectedTeam = ref(null);
const selectedAgentIdsOnThisFocus = ref(new Set());

const toggleTeamSelection = (option) => {
  selectedTeam.value = option;
  isTeamMenuVisible.value = false;
};

const toggleAgentSelection = (option) => {
  const index = selectedAgentIds.value.findIndex(
    (agentId) => agentId === option.id
  );

  if (index != -1) {
    selectedAgentIds.value.splice(index, 1);
  } else {
    selectedAgentIds.value.push(option.id);
    selectedAgentIdsOnThisFocus.value.add(option.id);
  }
};

const toggleTeamMenuVisible = () => {
  isTeamMenuVisible.value = !isTeamMenuVisible.value;
  if (isTeamMenuVisible.value) {
    isAgentMenuVisible.value = false;
  }
};

const toggleAgentMenuVisible = () => {
  isAgentMenuVisible.value = !isAgentMenuVisible.value;
  if (isAgentMenuVisible.value) {
    isTeamMenuVisible.value = false;
  }
};

const getTeamTooltip = (team) => {
  const NO_TEAM = "No Assigned Team";
  if (team) {
    return team.name;
  } else {
    return NO_TEAM;
  }
};

const compareUserName = (a, b) => a.name.localeCompare(b.name);
const filteredAgentOptions = computed(() => {
  const selectedAgentIdSet = new Set(selectedAgentIds.value);

  const initialSelectedAgentOptionObjs = selectedAgentIds.value
    .filter(
      (userId) =>
        !selectedAgentIdsOnThisFocus.value.has(userId) &&
        userId in userStore.allDict
    )
    .map((userId) => {
      const { id, name, shortName } = userStore.allDict[userId];
      return {
        id,
        name,
        shortName,
        selected: true,
      };
    })
    .sort(compareUserName);

  const remainingAgentOptionObjs = props.agentList
    .filter(
      (user) =>
        !selectedAgentIdSet.has(user.id) ||
        selectedAgentIdsOnThisFocus.value.has(user.id)
    )
    .map(({ id, name, shortName }) => ({
      id,
      name,
      shortName,
      selected: selectedAgentIdSet.has(id),
    }))
    .sort(compareUserName);

  return [
    ...initialSelectedAgentOptionObjs,
    ...remainingAgentOptionObjs,
  ].filter((option) =>
    option.name.toLowerCase().includes(searchAgentQuery.value.toLowerCase())
  );
});

const filteredTeamOptions = computed(() => {
  return props.teamList
    .filter((team) =>
      team.name.toLowerCase().includes(searchTeamQuery.value.toLowerCase())
    )
    .map((team) => {
      return {
        ...team,
        selected: selectedTeam.value?._id === team._id,
      };
    })
    .sort((teamA, teamB) => {
      if (teamA.selected) return -1;
      else if (teamB.selected) return 1;
      else return compareUserName(teamA, teamB);
    });
});

const searchAgentPlaceholder = computed(() => {
  return `Search ${props.agentList.length} Agents...`;
});

const searchTeamPlaceholder = computed(() => {
  return `Search ${props.teamList.length} Teams...`;
});

watch(isAgentMenuVisible, (isVisible) => {
  if (isVisible) return;
  if (!arraysEqual(selectedAgentIds.value, props.preselectedAgents)) {
    emit("update-assignees", {
      assignees: selectedAgentIds.value,
      teams: [],
    });
  }
  selectedAgentIdsOnThisFocus.value.clear();
  searchAgentQuery.value = "";
});

watch(isTeamMenuVisible, (curr) => {
  if (curr) return;

  const teamsChanged =
    selectedTeam.value &&
    !arraysEqual([selectedTeam.value._id], props.preselectedTeams);

  if (teamsChanged) {
    emit("update-teams", {
      teams: [selectedTeam.value._id],
      assignees: [],
    });
  }
  searchTeamQuery.value = "";
});

watch(
  () => props.preselectedAgents,
  (val) => {
    if (!val || !val.length) {
      selectedAgentIds.value = [];
      return;
    }
    selectedAgentIds.value = val.filter(
      (userId) => userId in userStore.allDict
    );
  },
  {
    immediate: true,
  }
);

watch(
  () => props.preselectedTeams,
  (val) => {
    if (!val || !val.length) {
      selectedTeam.value = null;
      return;
    }

    if (!props.showTeamsTab) {
      return;
    }

    selectedTeam.value = teamStore.allDict[val[0]];
  },
  {
    immediate: true,
  }
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

const handleClickOutside = (event) => {
  const dropdown = document.querySelector(
    ".assignee-dropdown .dropdown-menu-custom"
  );
  if (dropdown && !dropdown.contains(event.target)) {
    isAgentMenuVisible.value = false;
    isTeamMenuVisible.value = false;
  }
};

const slicedSelectedAgentsObjs = computed(() => {
  const NUM_SHOWN_AGENTS = 1;
  const slicedObjs = selectedAgentIds.value
    .slice(0, NUM_SHOWN_AGENTS)
    .map((id) => {
      const user = userStore.allDict[id];
      return {
        ...user,
        tooltip: user.name,
      };
    });

  const lenDiff = selectedAgentIds.value.length - slicedObjs.length;
  if (lenDiff > 0) {
    const excludedAgentIds = selectedAgentIds.value.slice(NUM_SHOWN_AGENTS);
    slicedObjs.push({
      shortName: `+${lenDiff}`,
      tooltip: excludedAgentIds
        .map((userId) => userStore.allDict[userId].name)
        .join(", "),
    });
  }

  return slicedObjs;
});
</script>

<template>
  <div class="d-flex justify-content-between">
    <div
      @click.stop="toggleAgentMenuVisible"
      class="assignee-buttons position-relative d-flex align-items-center"
    >
      <user-avatar
        v-for="(agent, index) in slicedSelectedAgentsObjs"
        :short-name="agent.shortName"
        :tooltip="agent.tooltip"
        icon-position="top-right"
        :iconVisible="index === slicedSelectedAgentsObjs.length - 1"
        :class="{
          'available-member': !!selectedTeam,
          'active-avatar': isAgentMenuVisible,
        }"
        :style="index ? { 'margin-left': '-8px' } : {}"
        size="medium"
        theme="light"
      >
        <template #icon>
          <div
            class="team-wrapper d-flex justify-content-center"
            @click.stop="toggleTeamMenuVisible"
            v-b-tooltip.hover.top="getTeamTooltip(selectedTeam)"
          >
            <div
              class="add__to-team d-flex align-items-center justify-content-center"
              v-if="!selectedTeam"
            >
              +
            </div>
            <div
              v-else
              class="current__user d-flex align-items-center justify-content-center"
            >
              {{ selectedTeam.shortName }}
            </div>
            <div
              class="arrow-loading align-items-center justify-content-center"
            >
              <IconArrowLoading></IconArrowLoading>
            </div>
          </div>
        </template>
      </user-avatar>

      <user-avatar
        icon-position="top-right"
        class="user-avatar__dashed"
        :class="{
          'available-member': !!selectedTeam,
          'active-avatar': isAgentMenuVisible,
        }"
        v-if="!selectedAgentIds.length"
        theme="transparent"
        size="medium"
      >
        <BIconPerson />
        <template #icon>
          <div
            class="team-wrapper d-flex justify-content-center"
            @click.stop="toggleTeamMenuVisible"
            v-b-tooltip.hover.top="getTeamTooltip(selectedTeam)"
          >
            <div
              class="add__to-team d-flex align-items-center justify-content-center"
              v-if="!selectedTeam"
            >
              +
            </div>
            <div
              v-else
              class="current__user d-flex align-items-center justify-content-center"
            >
              {{ selectedTeam.shortName }}
            </div>
            <div
              class="arrow-loading align-items-center justify-content-center"
            >
              <IconArrowLoading></IconArrowLoading>
            </div>
          </div>
        </template>
      </user-avatar>
    </div>
    <BDropdown
      end
      no-caret
      class="assignee-dropdown"
      :auto-close="false"
      v-model="isAgentMenuVisible"
    >
      <div @click.stop class="dropdown-menu-custom">
        <div class="p-2">
          <text-input
            name="search-query"
            type="search"
            v-model="searchAgentQuery"
            :placeholder="searchAgentPlaceholder"
          />
        </div>

        <div class="px-2 list">
          <div
            v-for="option in filteredAgentOptions"
            :key="option.id"
            class="option-card d-flex justify-content-between align-items-center mb-1"
            :class="{ active: option.selected }"
            @click.stop="toggleAgentSelection(option)"
          >
            <div class="d-flex align-items-center">
              <user-avatar
                :short-name="option.shortName"
                :tooltip="option.name"
                theme="light"
              />
              <span class="ms-2 text-break font-size-sm">{{
                option.name
              }}</span>
            </div>
            <BIconCheck v-if="option.selected" />
          </div>
        </div>
      </div>
    </BDropdown>
    <BDropdown
      end
      no-caret
      class="assignee-dropdown"
      :auto-close="false"
      v-model="isTeamMenuVisible"
    >
      <div @click.stop class="dropdown-menu-custom">
        <div class="p-2">
          <text-input
            name="search-query"
            type="search"
            v-model="searchTeamQuery"
            :placeholder="searchTeamPlaceholder"
          />
        </div>

        <div class="px-2 list">
          <div
            v-for="option in filteredTeamOptions"
            :key="option.id"
            class="option-card d-flex justify-content-between align-items-center p-2 mb-2"
            :class="{ active: option.selected }"
            @click.stop="toggleTeamSelection(option)"
          >
            <div class="d-flex align-items-center">
              <user-avatar
                :short-name="option.shortName"
                :tooltip="option.name"
                theme="light"
              />
              <span class="ms-2 text-break font-size-sm">{{
                option.name
              }}</span>
            </div>
            <BIconCheck v-if="option.selected" />
          </div>
        </div>
      </div>
    </BDropdown>
  </div>
</template>

<style lang="scss">
.extra-assignee-count {
  margin-left: 30px;
}

.assignee-buttons {
  .icon-marker {
    height: 16px !important;
    border-radius: 8px;
    border: 1px dashed #d9d9d9;
    background: #fff;
    color: #595959;
    font-size: 10px;
    font-weight: 600;
    line-height: 120%;
    bottom: 29px !important;
    left: 21px !important;
  }

  .active .icon-marker {
    width: 42px !important;
    border-radius: 8px;
    border: 1px solid #c5e1f8;
    background: #f5fbff !important;
    .add__to-team {
      width: 50% !important;
    }
    .current__user {
      width: 40% !important;
    }
    .arrow-loading {
      width: 40% !important;
      display: flex !important;
    }
  }
  .available-member .icon-marker {
    border: 1px solid #c5e1f8;
    background: #fff;
    .current__user {
      width: 100%;
      height: 100%;
    }
    &:hover {
      width: 42px;
      border-radius: 8px;
      border: 1px solid #c5e1f8;
      background: #f5fbff;
      .add__to-team {
        width: 50%;
      }
      .current__user {
        width: 40%;
      }
      .arrow-loading {
        width: 40%;
        display: flex;
      }
    }
  }
  .team-wrapper {
    height: 100%;
    .add__to-team {
      width: 100%;
    }
  }
  .arrow-loading {
    display: none;
  }
  .user-avatar__dashed .avatar {
    border-style: dashed !important;
  }
  .active-avatar {
    border: 1px solid var(--primary-150);
  }
  .avatar-wrapper {
    margin-right: -4px;

    .avatar:hover {
      border: 1px solid var(--primary-150);
    }

    //&:first-of-type {
    //  z-index: 2;
    //}
    //
    //&:nth-of-type(2) {
    //  z-index: 1;
    //}
    //
    //&:nth-of-type(3) {
    //  z-index: 0;
    //}
  }

  .tooltip {
    width: max-content;

    .tooltip-inner {
      span {
        &:last-of-type {
          color: $neutral-250;
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
        }
        &:first-of-type {
          color: white;
          font-size: 14px;
          font-style: normal;
          font-weight: 600;
          line-height: 140%;
        }
      }
    }
  }

  .seat {
    background: white;
    font-weight: 600;
    border-radius: 8px;
    font-size: 10px;
    border: 1px solid var(--primary-100);
    color: $neutral-350;
    padding: 2px 8px;
  }
}

.assignee-dropdown {
  .dropdown-menu {
    overflow: hidden;
    width: 17.5rem;
    max-height: 22.5rem !important;
    border-radius: 8px;
    margin-top: 4px;
    box-shadow: 0 2px 12px 0 $box-shadow-color;
  }

  .input::placeholder {
    color: rgba(0, 0, 0, 0.4);
  }

  .dropdown-toggle {
    visibility: hidden;
    position: absolute;
    width: 1px;
    padding: 0;
    bottom: 0;
  }

  .list {
    max-height: 15.625rem;
    overflow: auto;
  }

  .option-card {
    border-radius: 8px;
    cursor: pointer;
    color: $neutral-400;
    font-weight: 400;
    border: 1px solid transparent;
    font-size: 1rem;
    padding: 8px 12px;

    &:hover {
      border: 1px solid $bluish-gray-100;
      background: $bluish-gray-50;
    }

    &.active {
      border: 1px solid $bluish-gray-300;
      background: $bluish-gray-100;

      svg {
        width: 24px;
        height: 24px;
        fill: var(--primary-250);
      }
    }
  }
}
</style>
